import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';
import { apiCallBegan } from 'client/store/middleware/api/api';
import { RootState } from 'client/store/configureStore';
import { HierarchyLevelType, IDesign } from 'common/interfaces/hierarchy-entities';

interface DesignSyncCandidatesState {
  parentId: string | null;
  items: IDesign[];
  isLoading: boolean;
}

const initialState: DesignSyncCandidatesState = {
  parentId: null,
  items: [],
  isLoading: false,
};

const designSyncCandidatesSlice = createSlice({
  name: 'designSyncCandidates',
  initialState,
  reducers: {
    candidatesRequested: (state, action: PayloadAction<{ parentId: string }>) => {
      const { parentId } = action.payload;
      if (state.parentId !== parentId) {
        state.items = [];
      }
      state.parentId = parentId;
      state.isLoading = true;
    },
    candidatesReceived: (state, action: PayloadAction<{ parentId: string, records: IDesign[] }>) => {
      const { parentId, records } = action.payload;
      if (state.parentId !== parentId) return;
      state.items = records;
      state.isLoading = false;
    },
    candidatesFailed: (state) => {
      state.isLoading = false;
    },
  },
});

const { candidatesRequested, candidatesReceived, candidatesFailed } = designSyncCandidatesSlice.actions;

export default designSyncCandidatesSlice.reducer;

const designSyncCandidatesSelector = ({ entities }: RootState) => {
  return entities.designSyncCandidates;
};

export const getDesignSyncCandidates = createSelector(
  [designSyncCandidatesSelector, (_, parentId: string) => parentId],
  ({ parentId: loadedParentId, items, isLoading }, parentId) => {
    return loadedParentId === parentId ? { items, isLoading } : { items: [], isLoading: false };
  },
);

export const fetchDesignSyncCandidates = (parenttype: HierarchyLevelType, parentid: string) => {
  return apiCallBegan({
    url: '/design',
    method: 'GET',
    params: { parenttype, parentid },
    toastOnError: true,
    onStart: () => ({ type: candidatesRequested.type, payload: { parentId: parentid } }),
    onSuccess: (records: IDesign[]) => ({ type: candidatesReceived.type, payload: { parentId: parentid, records } }),
    onError: () => ({ type: candidatesFailed.type }),
  });
};
